import React, { ChangeEvent, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Box from '@node-space/storybook-components/dist/Box'
import ImageIcon from '@node-space/storybook-components/dist/ImageIcon'
import { TableRow } from '@node-space/storybook-components/dist/SimpleTable'
import { SimpleTableV2 } from '@node-space/storybook-components/dist/SimpleTableV2'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import Text from '@node-space/storybook-components/dist/Text'
import { DateFormat } from 'constants/General'
import { useApprovalsTransactionsQuery } from 'hooks/queries/useApprovalsTransactionsQuery'
import { ApprovalStatuses } from 'types/approvals'
import { SelectOption } from 'types/types'
import mockInitiatorOptions from '../../../../../__mocks__/Approvals/initiatorOptions.json'
import ApprovalFilterBar from '../ApprovalFilterBar'

const rowHeaderWidth = 'px-8 w-1/4'

interface ApprovedTransactionsProps {
  filterCurrencyOptions: SelectOption[]
  accountReference: string
}

export const ApprovedTransactions = ({
  filterCurrencyOptions,
  accountReference,
}: ApprovedTransactionsProps) => {
  const { t } = useTranslation()

  // TODO: filterbar search functionality with BE integration in phase 3
  const [searchTerm, setSearchTerm] = useState('')
  const [searchCurrency, setSearchCurrency] = useState('')

  const {
    data: approvedTransactions,
    isError,
    isFetching,
  } = useApprovalsTransactionsQuery(accountReference, ApprovalStatuses.APPROVED)
  const initiatorOptions = mockInitiatorOptions

  const onSearchQueryChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchCurrency(e.currentTarget.value)
  }

  const transactionsData = (): TableRow[] => {
    return approvedTransactions?.map(approvalTransaction => ({
      rowData: [
        {
          cellData: (
            <Box flex alignItems="center" gap={12} className="relative">
              <ImageIcon
                width={24}
                height={24}
                hasBackgroundColor={false}
                src={`${
                  process.env.ICONS_URL
                }${approvalTransaction?.currency?.code?.toLowerCase()}.svg`}
              />
              <Box className="relative">
                <Text size="sm" className="text-500">
                  {`${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`}
                </Text>
                <Text size="sm" color="disabled">
                  {approvalTransaction?.walletName}
                </Text>
              </Box>
            </Box>
          ),
          className: `py-4 px-8`,
        },
        {
          cellData: (
            <Box>
              <Text size="sm" className="text-500">
                {approvalTransaction?.fiatWithdrawalDetails?.accountNumber}
              </Text>
              <Text size="sm" color="disabled">
                {approvalTransaction?.fiatWithdrawalDetails?.beneficiaryName}
              </Text>
            </Box>
          ),
        },
        {
          cellData: (
            <Box>
              <Text size="sm" className="text-500">
                {approvalTransaction?.actionedBy}
              </Text>
              <Text size="sm" color="disabled">
                {dayjs(approvalTransaction?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
              </Text>
            </Box>
          ),
          className: `px-8`,
        },
      ],
    }))
  }

  const approvedTransactionTableInfo = transactionsData()

  return (
    <>
      {isError ? (
        <StatusCard status="error" headline={t('oopsSomethingWentWrong')} />
      ) : !isFetching && !approvedTransactionTableInfo?.length ? (
        <StatusCard
          headline={t('approvals.emptyStateHeadline', {
            approvalStatus: ApprovalStatuses.APPROVED.toLowerCase(),
          })}
          description={t('approvals.emptyStateDescription', {
            approvalStatus: ApprovalStatuses.APPROVED.toLowerCase(),
          })}
        />
      ) : (
        <>
          <ApprovalFilterBar
            searchTerm={searchTerm}
            onSearchTermChange={onSearchQueryChanged}
            searchCurrency={searchCurrency}
            onSearchCurrencyChange={currency => setSearchCurrency(currency)}
            initiatorOptions={initiatorOptions}
            currencyOptions={filterCurrencyOptions}
          />
          <SimpleTableV2
            columns={[
              {
                headerTitle: t('payment'),
                className: rowHeaderWidth,
              },
              {
                headerTitle: t('to'),
              },
              {
                headerTitle: t('approvals.approvedBy'),
                className: rowHeaderWidth,
              },
            ]}
            isLoading={isFetching}
            tableData={approvedTransactionTableInfo}
          />
        </>
      )}
    </>
  )
}
